import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";
import { numericProfile } from "@/lib/coverage";
import { Input, Label } from "@/components/ui-kit/Input";
import { useFunder } from "@/funder/FunderContext";

function Row({ label, value, strong, accent }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className={accent ? "text-sm text-muted-foreground" : "text-sm text-muted-foreground"}>
        {label}
      </span>
      <span
        className={
          "nums tabular-nums text-sm " +
          (strong ? "font-semibold text-foreground" : "text-foreground")
        }
      >
        {money(value)}
      </span>
    </div>
  );
}

/**
 * Live coverage preview. Calls POST /api/coverage/preview (no persistence) whenever
 * the rate / rounds / profile / retainer / hours change, so Linda can sanity-check a
 * rule set before saving.
 */
export function CoveragePreview({ rate, rounds, profile, retainer, isAcsa, valid }) {
  const { abbr } = useFunder();
  const [hours, setHours] = useState("17");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!valid) {
      setError("Fix the highlighted fields to preview.");
      setResult(null);
      return undefined;
    }
    const hrs = Number(hours);
    if (Number.isNaN(hrs) || hrs < 0) {
      setError("Enter valid test hours.");
      return undefined;
    }
    let active = true;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .post("/coverage/preview", {
          rate: Number(rate) || 0,
          rounds: isAcsa ? rounds : [],
          profile: numericProfile(profile),
          retainer: Number(retainer) || 0,
          hours: hrs,
        })
        .then((r) => {
          if (!active) return;
          setResult(r.data);
          setError("");
        })
        .catch((e) => active && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
        .finally(() => active && setLoading(false));
    }, 350);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [rate, rounds, profile, retainer, hours, isAcsa, valid]);

  return (
    <div data-testid="coverage-preview">
      <div className="mb-4">
        <Label htmlFor="preview-hours">Test hours</Label>
        <Input
          id="preview-hours"
          type="number"
          step="any"
          min="0"
          data-testid="preview-hours-input"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          Hypothetical billable hours — nothing is saved.
        </p>
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/60 px-3 py-2 text-sm text-muted-foreground">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      ) : !result ? (
        <div className="grid place-items-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : (
        <div className={loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
          <Row label="Charges to date" value={result.charges_to_date} />
          {isAcsa && (
            <>
              <div className="my-2 border-t border-border" />
              <Row label="Level 1 credit" value={result.l1} />
              <Row label="Level 2 credit" value={result.l2} />
              <Row label="Level 3 credit" value={result.l3} />
              <div className="my-2 border-t border-border" />
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm font-medium text-status-paid">{abbr} coverage</span>
                <span
                  data-testid="preview-acsa-total"
                  className="nums text-base font-semibold text-status-paid"
                >
                  {money(result.acsa_coverage)}
                </span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between py-1.5">
            <span className="text-sm text-muted-foreground">Client portion</span>
            <span className="nums text-sm text-foreground" data-testid="preview-client-portion">
              {money(result.client_portion)}
            </span>
          </div>
          {Number(retainer) > 0 && (
            <Row label="Retainer applied" value={-Number(retainer)} />
          )}
          <div className="mt-1 flex items-center justify-between rounded-md bg-primary/8 px-3 py-2">
            <span className="text-sm font-medium text-foreground">Client responsibility</span>
            <span
              data-testid="preview-client-responsibility"
              className="nums text-base font-semibold text-primary"
            >
              {money(result.client_responsibility)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
